import { useRef } from 'react';
import { addMonths } from 'date-fns';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { actions } from 'store';
import { breakpoints } from 'styles';
import { ModalButtonWithForm } from 'components/common';
import {
  Datepicker,
  FormGroup,
  MultipleAttachments,
  TextArea
} from 'components/common/form';
import { ProductUserCustomAttributeRenderFormField } from 'components/productUser';

import type { FormApi } from 'informed';
import type { Attachments } from 'shared-types';
import type { GetAchievementsRes } from 'shared-types/responses';

type FormValues = {
  attachments: NonNullable<Attachments>;
  completionDate?: Date;
  comments: string;
  expirationDate?: Date;
  startedAtDate?: Date;
};

const getAutomaticExpirationDate = (
  date: number,
  monthsBeforeExpiration: number
) =>
  date && monthsBeforeExpiration
    ? addMonths(new Date(date), monthsBeforeExpiration).getTime()
    : date;

interface Props {
  achievement: Pick<
    GetAchievementsRes['achievements'][0],
    | 'id'
    | 'certificateAllowAdding'
    | 'certificateRequiredFromUser'
    | 'monthsBeforeExpiration'
    | 'type'
    | 'userAttributes'
  >;
  inDropdown?: boolean;
  initialValues: GetAchievementsRes['achievements'][0]['pendingRequest'];
  updateAchievements: () => Promise<void>;
}

const AchievementCompletionEditSelf = ({
  achievement,
  inDropdown = false,
  initialValues,
  updateAchievements
}: Props) => {
  const { dispatch } = useDispatch();

  const { language, messages, user } = useReduxState();

  const formApi = useRef<FormApi<FormValues>>(undefined);
  const uploader = useRef<() => Promise<Attachments>>(undefined);

  const handleCompletionDateChange = (value: number) => {
    if (achievement.monthsBeforeExpiration)
      formApi.current?.setValue(
        'expirationDate',
        getAutomaticExpirationDate(value, achievement.monthsBeforeExpiration)
      );
  };

  const handleSubmit = async (values: FormValues) => {
    try {
      const attachments = achievement.certificateAllowAdding
        ? await uploader.current()
        : null;

      if (initialValues.comments && !values.comments) values.comments = null;

      const userAttributes = {};

      Object.keys(values).forEach((fieldKey) => {
        if (fieldKey.startsWith('userAttributes-')) {
          const attributeId = fieldKey.split('-')[1];
          userAttributes[attributeId] = values[fieldKey];
        }
      });

      await api.post(
        `/admin/achievements/${achievement.id}/request-access/${initialValues.id}`,
        {
          attachments,
          comments: values.comments,
          completionDate: values.completionDate,
          expirationDate: values.expirationDate,
          startedAtDate: values.startedAtDate,
          userAttributes
        }
      );

      dispatch(
        actions.addNotice({
          text: messages.commonSavedSuccessfully,
          type: 'success'
        })
      );

      await updateAchievements();
    } catch (err) {
      if (err.message === messages.commonUploadFileFailed) {
        return false; // keep the modal open, user already notified by the uploader
      } else {
        await dispatch.handleErrorDispatch(err);
      }
    }
  };

  return !user ? null : (
    <ModalButtonWithForm
      buttonIcon="edit"
      buttonIsDropdown={inDropdown}
      buttonProps={{ isRounded: true }}
      buttonSize="medium"
      buttonText={messages.accessRequestEdit}
      buttonTextHideBreakpoint={breakpoints.medium - 1}
      initialValues={Object.keys(initialValues).reduce((fields, field) => {
        if (field === 'userAttributes') {
          Object.keys(initialValues[field]).forEach((attrId) => {
            fields[`userAttributes-${attrId}`] = initialValues[field][attrId];
          });
        } else {
          fields[field] = initialValues[field];
        }
        return fields;
      }, {})}
      modalActionCategory="success"
      modalActionText={messages.commonSave}
      modalAutoFocus={false} // Disable auto focus on the first input field since it's an optional datepicker
      modalTitle={messages.accessRequestEdit}
      modalWidth="medium"
      onSubmit={handleSubmit}
      setFormApi={(form: FormApi<FormValues>) => (formApi.current = form)}
      stopPropagation={true}
    >
      {({ values }: { values: FormValues }) => (
        <div className="inner">
          {['external', 'milestone'].includes(achievement.type) ? (
            <div className="dates">
              {achievement.type === 'external' ? (
                <FormGroup
                  label={messages.commonStartDate}
                  labelIsOptional={true}
                >
                  <Datepicker
                    field="startedAtDate"
                    isClearable={true}
                    placeholder={messages.publishStartDateEmpty}
                    maxDate={values.completionDate || null}
                    simpleClear={true}
                  />
                </FormGroup>
              ) : null}

              <FormGroup
                label={messages.excelCompletionDate}
                style={{ margin: 0 }}
              >
                <Datepicker
                  field="completionDate"
                  isClearable={false}
                  onChange={handleCompletionDateChange}
                  placeholder={messages.publishStartDateEmpty}
                  maxDate={values.expirationDate || null}
                  simpleClear={true}
                />
              </FormGroup>

              {achievement.type === 'external' ? (
                <FormGroup
                  label={messages.commonExpirationDate}
                  labelIsOptional={!achievement.monthsBeforeExpiration}
                  style={{ margin: 0 }}
                >
                  <div
                    {...(achievement.monthsBeforeExpiration && {
                      'aria-label':
                        messages.achievementCompletionAutomaticExpirationTooltip,
                      'data-tooltip':
                        messages.achievementCompletionAutomaticExpirationTooltip
                    })}
                  >
                    <Datepicker
                      disabled={achievement.monthsBeforeExpiration}
                      field="expirationDate"
                      isClearable={!Boolean(achievement.monthsBeforeExpiration)}
                      placeholder={messages.publishEndDateEmpty}
                      minDate={values.completionDate || null}
                      simpleClear={true}
                    />
                  </div>
                </FormGroup>
              ) : null}
            </div>
          ) : null}

          <FormGroup label={messages.commonComment} labelIsOptional={true}>
            <TextArea
              autoGrow={true}
              field="comments"
              rows={3}
              style={{ minWidth: 200 }}
            />
          </FormGroup>

          {achievement.certificateAllowAdding && (
            <div className="user-uploader">
              <MultipleAttachments
                getUploadHandler={(fn) => (uploader.current = fn)}
                required={achievement.certificateRequiredFromUser}
              />
            </div>
          )}

          {achievement.userAttributes?.map((attribute) => (
            <FormGroup
              key={attribute.id}
              label={attribute.titleTranslations?.[language] || attribute.title}
              labelIsOptional={!attribute.isRequired}
            >
              <ProductUserCustomAttributeRenderFormField
                attribute={attribute}
              />
            </FormGroup>
          ))}

          <style jsx>{`
            .dates {
              align-items: flex-start;
              display: flex;
              gap: 30px;
            }
            .user-uploader {
              margin-top: 20px;
              width: max-content;
            }
          `}</style>
        </div>
      )}
    </ModalButtonWithForm>
  );
};

export default AchievementCompletionEditSelf;
